import dayjs from "dayjs";
import { BaseControlledModal } from "../../../../../../common/components/base_controlled_modal";
import { GryfoAudit } from "../../../../../gestaoEpi/domain/models/gryfo_audit";
import { TermoEpi } from "../../../../../gestaoEpi/domain/models/termo_epi";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useState } from "react";

type Props = {
  open: boolean;
  loading: boolean;
  imagemUrl: string | null;
  auditoriaGryfo: GryfoAudit | null;
  selectedTermo: TermoEpi | null;
  onClose: () => void;
};

export const VisualizacaoGryfoAuditaModal = ({
  open,
  loading,
  imagemUrl, 
  auditoriaGryfo,
  selectedTermo,
  onClose,
}: Props) => {
  const [modalTermoAberto, setModalTermoAberto] = useState<boolean>(false);

  const formatarDataAuditoria = (timestamp?: number) => {
    return timestamp
      ? dayjs.unix(timestamp).format("DD/MM/YYYY [às] HH:mm:ss")
      : "-";
  };

  const isFraude = auditoriaGryfo?.gryfo_evaluation !== "NOT_A_FRAUD";

  const imgSrc = auditoriaGryfo?.imagemBase64
    ? `data:image/jpeg;base64,${auditoriaGryfo.imagemBase64}`
    : imagemUrl;

  return (
    <>
      <BaseControlledModal
        open={open}
        saving={false}
        loading={loading}
        title="Detalhes da Auditoria Biométrica"
        width="700px"
        onClose={onClose}
      >
        <div className="w-full flex flex-col gap-4 p-4 bg-gray-50 min-h-[300px] rounded-md mt-4">
          {loading ? (
            <span className="text-gray-500 text-center flex-1 flex items-center justify-center">
              Carregando dados da auditoria...
            </span>
          ) : auditoriaGryfo ? (
            <div className="flex flex-col md:flex-row gap-6 w-full">
              {/* LADO ESQUERDO: Imagem */}
              <div className="flex flex-col items-center w-full md:w-1/2">
                <div className="w-full bg-white p-2 rounded-md shadow-sm border border-gray-200 flex justify-center mb-2">
                  {imgSrc ? (
                    <img
                      src={imgSrc}
                      alt="Biometria Auditada"
                      className="max-w-full max-h-[35vh] object-contain rounded-md"
                    />
                  ) : (
                    <span className="text-gray-400 py-10">
                      Imagem indisponível
                    </span>
                  )}
                </div>

                {/* Badge de Resultado Principal */}
                <div
                  className={`w-full py-2 text-center rounded-md font-bold text-white shadow-sm ${
                    isFraude ? "bg-red-500" : "bg-green-500"
                  }`}
                >
                  {isFraude ? "⚠️ POSSÍVEL FRAUDE" : "✅ AUTÊNTICO (SEM FRAUDE)"}
                </div>
              </div>

              {/* LADO DIREITO: Dados Técnicos da Auditoria */}
              <div className="flex flex-col w-full md:w-1/2 gap-3">
                <h3 className="font-semibold text-gray-700 border-b pb-1">
                  Informações de Segurança
                </h3>

                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">
                    Data e Hora do Reconhecimento
                  </span>
                  <span className="text-sm font-medium text-gray-800">
                    {formatarDataAuditoria(auditoriaGryfo.last_recognized)}
                  </span>
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">
                    Matrícula Reconhecida
                  </span>
                  <span className="text-sm font-medium text-gray-800">
                    {auditoriaGryfo.external_id || "-"}
                  </span>
                </div>

                {selectedTermo && (
                  <div className="flex flex-col gap-1 mt-2">
                    <span className="text-xs text-gray-500">Documento Vinculado</span>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      onClick={() => setModalTermoAberto(true)}
                      sx={{ textTransform: "none", width: "fit-content", fontWeight: "bold" }}
                    >
                      📄 Visualizar Termo Assinado
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <span className="text-gray-500 text-center flex-1 flex items-center justify-center h-full">
              Nenhum dado de auditoria encontrado.
            </span>
          )}
        </div>
      </BaseControlledModal>

      {/* 👇 MODAL SECUNDÁRIO PARA LER O TERMO */}
      {selectedTermo && (
        <Dialog
          open={modalTermoAberto}
          onClose={() => setModalTermoAberto(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: 9999 }} 
        >
          <DialogTitle className="bg-gray-50 border-b font-bold text-gray-800">
            Visualização do Termo
          </DialogTitle>
          <DialogContent className="py-6">
            <div className="flex flex-col gap-4 mt-2">
              <h2 className="font-bold text-lg text-gray-800">
                {selectedTermo.nome} 
              </h2>
              
              <div className="bg-gray-100 p-4 rounded text-sm text-gray-700 h-64 overflow-y-auto border border-gray-300">
                {selectedTermo.descricao ? (
                  <p style={{ whiteSpace: "pre-wrap" }}>{selectedTermo.descricao}</p>
                ) : (
                  <p className="italic text-gray-500 text-center mt-10">Texto do termo indisponível.</p>
                )}
              </div>
              
              <div className="bg-green-50 border border-green-200 text-green-800 text-xs p-3 rounded text-center font-semibold">
                ✅ Este termo foi aceito e assinado via validação biométrica.
              </div>
            </div>
          </DialogContent>
          
          <DialogActions className="p-4 border-t bg-gray-50">
            <Button
              onClick={() => setModalTermoAberto(false)}
              variant="contained"
              disableElevation
              sx={{ background: "#6b7280", "&:hover": { background: "#4b5563" } }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};